import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Trophy, Medal, Crown, Star, Users, UserPlus, Target, Award,
  MessageCircle, PenTool, Calendar, Flame, TrendingUp, ChevronRight,
  Zap, Shield, ArrowUp
} from 'lucide-react';
import { toast } from 'sonner';

const BADGE_ICONS = {
  'message-circle': MessageCircle,
  'users': Users,
  'network': Users,
  'user-plus': UserPlus,
  'trophy': Trophy,
  'pen-tool': PenTool,
  'crown': Crown,
  'calendar': Calendar,
  'star': Star,
};

const TIER_CONFIG = [
  { min: 0, name: 'Starter', color: 'text-slate-500', bg: 'bg-slate-100', border: 'border-slate-200', icon: Shield },
  { min: 5, name: 'Bronze', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: Medal },
  { min: 15, name: 'Silver', color: 'text-slate-400', bg: 'bg-slate-50', border: 'border-slate-300', icon: Medal },
  { min: 30, name: 'Gold', color: 'text-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-300', icon: Trophy },
  { min: 50, name: 'Platinum', color: 'text-cyan-500', bg: 'bg-cyan-50', border: 'border-cyan-300', icon: Crown },
];

const getTier = (score) => {
  for (let i = TIER_CONFIG.length - 1; i >= 0; i--) {
    if (score >= TIER_CONFIG[i].min) return TIER_CONFIG[i];
  }
  return TIER_CONFIG[0];
};

const getNextTier = (score) => {
  for (const tier of TIER_CONFIG) {
    if (score < tier.min) return tier;
  }
  return null;
};

const RankBadge = ({ rank }) => {
  if (rank === 1) return <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-200"><Crown className="w-5 h-5 text-white" /></div>;
  if (rank === 2) return <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center shadow-lg shadow-slate-200"><Medal className="w-5 h-5 text-white" /></div>;
  if (rank === 3) return <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-600 to-amber-700 flex items-center justify-center shadow-lg shadow-amber-200"><Medal className="w-5 h-5 text-white" /></div>;
  return <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center"><span className="text-sm font-bold text-muted-foreground">#{rank}</span></div>;
};

const BadgeCard = ({ badge, earned }) => {
  const Icon = BADGE_ICONS[badge.icon] || Star;
  return (
    <div
      className={`p-4 rounded-xl border-2 transition-all ${
        earned
          ? 'border-primary/30 bg-primary/5 shadow-sm'
          : 'border-dashed border-muted-foreground/20 bg-muted/30 opacity-60'
      }`}
      data-testid={`badge-${badge.id}`}
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
        earned ? 'bg-primary/10' : 'bg-muted'
      }`}>
        <Icon className={`w-6 h-6 ${earned ? 'text-primary' : 'text-muted-foreground/40'}`} />
      </div>
      <h4 className={`text-sm font-semibold ${earned ? '' : 'text-muted-foreground'}`}>{badge.name}</h4>
      <p className="text-xs text-muted-foreground mt-0.5">{badge.description}</p>
      {earned && (
        <Badge className="mt-2 bg-green-100 text-green-700 text-[10px]">Earned</Badge>
      )}
    </div>
  );
};

const ProgressCard = ({ item }) => {
  const pct = Math.min(100, Math.round((item.current / item.target) * 100));
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-background" data-testid={`progress-${item.badge}`}>
      <div className="flex-1">
        <p className="text-sm font-medium">{item.label}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-purple-500 rounded-full transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-primary">{item.current}/{item.target}</span>
        </div>
      </div>
      <Zap className="w-4 h-4 text-amber-500 flex-shrink-0" />
    </div>
  );
};

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [badges, setBadges] = useState([]);
  const [progress, setProgress] = useState([]);
  const [totalBadges, setTotalBadges] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leaderboard');

  const fetchData = useCallback(async () => {
    try {
      const [lbData, badgeData] = await Promise.all([
        api.getLeaderboard(),
        api.getMyBadges(),
      ]);
      setLeaderboard(lbData.leaderboard || []);
      setMyRank(lbData.my_rank);
      setBadges(badgeData.badges || []);
      setProgress(badgeData.progress || []);
      setTotalBadges(badgeData.total_badges || 0);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const myScore = leaderboard.find(l => l.is_me)?.score || 0;
  const tier = getTier(myScore);
  const nextTier = getNextTier(myScore);
  const TierIcon = tier.icon;

  const ALL_BADGES = [
    { id: 'first_post', name: 'First Post', icon: 'message-circle', description: 'Made your first post' },
    { id: 'networker_5', name: 'Networker', icon: 'users', description: 'Connected with 5+ professionals' },
    { id: 'networker_10', name: 'Super Networker', icon: 'network', description: 'Connected with 10+ professionals' },
    { id: 'referrer_3', name: 'Referral Pro', icon: 'user-plus', description: 'Referred 3+ members' },
    { id: 'referrer_10', name: 'Growth Champion', icon: 'trophy', description: 'Referred 10+ members' },
    { id: 'signature_creator', name: 'Signature Creator', icon: 'pen-tool', description: 'Created your first email signature' },
    { id: 'group_leader', name: 'Community Leader', icon: 'crown', description: 'Created a group' },
    { id: 'event_host', name: 'Event Host', icon: 'calendar', description: 'Hosted an event' },
    { id: 'top_builder', name: 'Top Builder', icon: 'star', description: 'Monthly top network builder' },
  ];

  const earnedIds = new Set(badges.map(b => b.id));

  return (
    <Layout>
      <div className="space-y-6" data-testid="leaderboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Outfit'] flex items-center gap-2">
              <Trophy className="w-6 h-6 text-amber-500" /> Leaderboard
            </h1>
            <p className="text-muted-foreground text-sm">Compete, earn badges, and grow your network</p>
          </div>
          <div className="flex gap-2">
            {['leaderboard', 'badges'].map(tab => (
              <Button
                key={tab}
                variant={activeTab === tab ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab(tab)}
                className="capitalize text-xs"
                data-testid={`tab-${tab}`}
              >
                {tab === 'leaderboard' ? <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> : <Award className="w-3.5 h-3.5 mr-1.5" />}
                {tab}
              </Button>
            ))}
          </div>
        </div>

        {/* My Stats Summary */}
        <Card className={`${tier.border} border-2`} data-testid="my-stats-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl overflow-hidden border-2 border-primary/20 bg-muted flex-shrink-0">
                {user?.profile_photo_url ? (
                  <img src={resolveImageUrl(user.profile_photo_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/40 to-primary flex items-center justify-center">
                    <span className="text-white font-bold text-xl">{(user?.full_name || 'U')[0]}</span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-bold">{user?.full_name}</h2>
                  <Badge className={`${tier.bg} ${tier.color} text-[10px] border ${tier.border}`}>
                    <TierIcon className="w-3 h-3 mr-0.5" />{tier.name}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {myRank ? `Rank #${myRank}` : 'Unranked'} &middot; Score: {myScore} &middot; {totalBadges} badges earned
                </p>
              </div>
              <div className="text-right hidden sm:block">
                <p className="text-3xl font-bold text-primary">{myScore}</p>
                <p className="text-xs text-muted-foreground">Total Score</p>
              </div>
            </div>
            {nextTier && (
              <div className="mt-4 pt-4 border-t border-border/50">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-muted-foreground">Next tier: <span className={`font-semibold ${nextTier.color}`}>{nextTier.name}</span></span>
                  <span className="font-semibold">{myScore}/{nextTier.min}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-purple-500 rounded-full transition-all duration-700"
                    style={{ width: `${Math.min(100, (myScore / nextTier.min) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {activeTab === 'leaderboard' && (
          <div className="space-y-4">
            {/* Scoring Guide */}
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                  <Flame className="w-4 h-4 text-orange-500" /> How Scoring Works
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Connection', points: '+2 pts', icon: Users, color: 'text-blue-500 bg-blue-50' },
                    { label: 'Referral', points: '+3 pts', icon: UserPlus, color: 'text-green-500 bg-green-50' },
                    { label: 'Post', points: '+1 pt', icon: MessageCircle, color: 'text-purple-500 bg-purple-50' },
                    { label: 'Event', points: '+2 pts', icon: Calendar, color: 'text-rose-500 bg-rose-50' },
                  ].map(s => (
                    <div key={s.label} className="flex items-center gap-2 p-2.5 rounded-lg border border-border/50">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.color}`}>
                        <s.icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs font-medium">{s.label}</p>
                        <p className="text-[11px] text-muted-foreground">{s.points}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top 3 Podium */}
            {!loading && leaderboard.length >= 3 && (
              <div className="grid grid-cols-3 gap-3" data-testid="podium">
                {[1, 0, 2].map(idx => {
                  const entry = leaderboard[idx];
                  if (!entry) return null;
                  const rank = idx + 1;
                  const isFirst = rank === 1;
                  return (
                    <Card
                      key={entry.user_id}
                      className={`text-center ${isFirst ? 'border-amber-300 bg-gradient-to-b from-amber-50/50 -mt-4 pb-2' : 'border-border/50'} ${entry.is_me ? 'ring-2 ring-primary/30' : ''}`}
                      data-testid={`podium-${rank}`}
                    >
                      <CardContent className="p-4">
                        <RankBadge rank={rank} />
                        <div className="w-12 h-12 rounded-full mx-auto mt-3 overflow-hidden border-2 border-muted bg-muted">
                          {entry.photo ? (
                            <img src={resolveImageUrl(entry.photo)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-primary/20 flex items-center justify-center">
                              <span className="text-primary font-bold">{(entry.name || 'U')[0]}</span>
                            </div>
                          )}
                        </div>
                        <h4 className="text-sm font-semibold mt-2 line-clamp-1">{entry.name}</h4>
                        <p className="text-[11px] text-muted-foreground line-clamp-1">{entry.profession}</p>
                        <p className="text-lg font-bold text-primary mt-1">{entry.score}</p>
                        <p className="text-[10px] text-muted-foreground">{entry.partners} connections &middot; {entry.referrals} referrals</p>
                        {entry.is_me && <Badge className="mt-1 bg-primary/10 text-primary text-[10px]">You</Badge>}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Full Leaderboard List */}
            <Card>
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b border-border/50 bg-muted/30">
                  <h3 className="text-sm font-semibold">Full Rankings</h3>
                </div>
                {loading ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">Loading leaderboard...</div>
                ) : leaderboard.length === 0 ? (
                  <div className="p-8 text-center">
                    <Trophy className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">No rankings yet. Start connecting to climb the leaderboard!</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border/30">
                    {leaderboard.map((entry, i) => {
                      const entryTier = getTier(entry.score);
                      return (
                        <div
                          key={entry.user_id}
                          className={`flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors ${entry.is_me ? 'bg-primary/5' : ''}`}
                          data-testid={`leaderboard-row-${i}`}
                        >
                          <RankBadge rank={i + 1} />
                          <div className="w-9 h-9 rounded-full overflow-hidden bg-muted flex-shrink-0">
                            {entry.photo ? (
                              <img src={resolveImageUrl(entry.photo)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-primary/20 flex items-center justify-center">
                                <span className="text-primary font-bold text-sm">{(entry.name || 'U')[0]}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-semibold line-clamp-1">{entry.name}</p>
                              {entry.is_me && <Badge className="bg-primary/10 text-primary text-[9px] px-1.5">You</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {entry.profession} {entry.city ? `· ${entry.city}` : ''}
                            </p>
                          </div>
                          <Badge className={`${entryTier.bg} ${entryTier.color} text-[10px] border ${entryTier.border} hidden sm:flex`}>
                            {entryTier.name}
                          </Badge>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-primary">{entry.score}</p>
                            <p className="text-[10px] text-muted-foreground">{entry.partners}c · {entry.referrals}r</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'badges' && (
          <div className="space-y-6">
            {/* Badge Progress */}
            {progress.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                    <ArrowUp className="w-4 h-4 text-green-500" /> In Progress
                  </h3>
                  <div className="space-y-2">
                    {progress.map(p => <ProgressCard key={p.badge} item={p} />)}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Badges Grid */}
            <div>
              <h3 className="text-sm font-semibold mb-3">All Badges ({totalBadges}/{ALL_BADGES.length} earned)</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {ALL_BADGES.map(b => (
                  <BadgeCard key={b.id} badge={b} earned={earnedIds.has(b.id)} />
                ))}
              </div>
            </div>

            {/* CTA */}
            <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-purple-500/5">
              <CardContent className="p-5 text-center">
                <Trophy className="w-8 h-8 mx-auto text-primary mb-2" />
                <h3 className="text-sm font-semibold mb-1">Earn More Badges</h3>
                <p className="text-xs text-muted-foreground mb-4">Connect with professionals, refer members, and host events to unlock all badges!</p>
                <div className="flex gap-2 justify-center">
                  <Link to="/explore">
                    <Button size="sm" className="text-xs" data-testid="badge-cta-explore">
                      <Users className="w-3.5 h-3.5 mr-1" /> Explore Pros
                    </Button>
                  </Link>
                  <Link to="/signatures">
                    <Button size="sm" variant="outline" className="text-xs" data-testid="badge-cta-signature">
                      <PenTool className="w-3.5 h-3.5 mr-1" /> Create Signature
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
